/**
 * Waystation hub emotes — visual-only, no chat, no economy state.
 *
 * The standard set is free for every authenticated hub player and must match
 * the backend `STANDARD_HUB_EMOTES` ids. Every emote renders inside the same
 * thought-bubble frame (`emote_bubble_thought_01`) diagonally above-right of
 * the actor's head; the per-emote spritesheet animates inside the bubble.
 *
 * Sheets are horizontal strips of square frames under
 * `/assets/sprites/emotes/<assetId>.png` (mirrored from `assets/sprites/emotes`).
 */

export interface HubEmoteDef {
  /** Wire id sent to POST /hub/emote — must match the backend standard set. */
  id: string;
  /** Short button label for the Waystation emote panel. */
  label: string;
  /** Sprite asset id (also the PixelLab source folder name). */
  assetId: string;
  /** Horizontal frame count of the sheet. */
  frames: number;
  fps: number;
}

export const HUB_EMOTE_BUBBLE_ASSET_ID = 'emote_bubble_thought_01';

export const HUB_EMOTES: readonly HubEmoteDef[] = [
  { id: 'wave', label: 'Greeting', assetId: 'emote_greeting_wave_01', frames: 7, fps: 10 },
  { id: 'laugh', label: 'Laugh', assetId: 'emote_laugh_01', frames: 7, fps: 10 },
  { id: 'cry', label: 'Cry', assetId: 'emote_cry_01', frames: 7, fps: 10 },
  { id: 'monkey_cymbals', label: 'Cymbals', assetId: 'emote_monkey_cymbals_01', frames: 7, fps: 10 },
  { id: 'spinner_wheel', label: 'Spinner', assetId: 'emote_spinner_wheel_01', frames: 7, fps: 10 },
  { id: 'dungeon_doors', label: 'Doors', assetId: 'emote_dungeon_doors_01', frames: 7, fps: 10 },
];

export function hubEmoteById(id: string | null | undefined): HubEmoteDef | null {
  if (!id) return null;
  return HUB_EMOTES.find((e) => e.id === id) ?? null;
}

export function emoteSpriteUrl(assetId: string): string {
  return `/assets/sprites/emotes/${assetId}.png`;
}

/** Current sheet frame for an emote that started at `startedAtMs` (loops). */
export function emoteFrameAt(def: Pick<HubEmoteDef, 'frames' | 'fps'>, startedAtMs: number | null, nowMs: number): number {
  const elapsed = startedAtMs !== null && Number.isFinite(startedAtMs) ? Math.max(0, nowMs - startedAtMs) : Math.max(0, nowMs);
  const step = Math.floor((elapsed / 1000) * def.fps);
  return def.frames > 0 ? step % def.frames : 0;
}
